import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { WidgetsGet, UsersWidgetsAdd, UsersWidgetsDelete } from "apiServices/reducer/apiService";

const DashboardWidgetManager = (props) => {
  const dispatch = useDispatch();
  const reduxUserId = useSelector((state) => state.apiService?.login?.user_id);
  const userData = useSelector((state) => state.apiService.login);
  const menuData = useSelector((state) => state.apiService.sharedReportsValidateCode);
  const isAccess = menuData.role === 'User';
  const isProvider = !isAccess ? userData.user_type_name === 'Provider' : true;
  const userId = props.userId;
  const token = useSelector((state) => state.apiService?.token);

  const [trayWidgets, setTrayWidgets] = useState([]);
  const [dashboardWidgets, setDashboardWidgets] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [hoverTargetId, setHoverTargetId] = useState(null);
  const [activeDragId, setActiveDragId] = useState(null);
  const [dragClone, setDragClone] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTray, setShowTray] = useState(false);
  const dragRef = React.useRef(null);
  const dashboardWidgetsRef = React.useRef(dashboardWidgets);
  const cloneElRef = React.useRef(null);
  const rafRef = React.useRef(null);
  const latestPosRef = React.useRef({ x: 0, y: 0 });
  const params = {
    userId,
    token
  };

  // Fetch widgets from the backend
  const fetchWidgets = async () => {
    console.log(userId, "userId widgets")
    if (!userId || !token) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await dispatch(WidgetsGet(params)).unwrap();
      const tray = result.filter(w => !w.is_fixed && !w.is_selected);
      const dashboard = result.filter(w => w.is_fixed || w.is_selected);
      setTrayWidgets(tray);
      setDashboardWidgets(dashboard);
    } catch (err) {
      setError("Failed to load widgets.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWidgets();
    // eslint-disable-next-line
  }, [dispatch, userId, token, props.userId]);

  useEffect(() => {
    dashboardWidgetsRef.current = dashboardWidgets;
  }, [dashboardWidgets]);

  useEffect(() => {
    if (!dragClone) return;
    const node = cloneElRef.current;
    const draggedCard = node?.querySelector(".cardwidget") || node?.firstElementChild;
    if (draggedCard instanceof HTMLElement) {
      draggedCard.style.marginBottom = "0";
      draggedCard.style.boxShadow = "0 16px 36px rgba(0,0,0,0.30)";
    }
  }, [dragClone]);

  useEffect(() => {
    if (isDragging) {
      document.body.style.cursor = "move";
      document.body.style.userSelect = "none";
    } else {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging]);

  // Add widget to the dashboard
  const addWidgetToDashboard = async (widgetId) => {
    const widget = trayWidgets.find(w => w.widget_id === widgetId);
    if (!widget) return;
    try {
      const formBody = {
        UserId: userId,
        WidgetId: widget.widget_id,
        LoggedInUserId: userId,
        token: token
      };
      await dispatch(UsersWidgetsAdd(formBody)).unwrap();
      fetchWidgets();
    } catch (err) { }
  };

  // Remove widget from the dashboard
  const removeWidgetFromDashboard = async (widgetId) => {
    const widget = dashboardWidgets.find(w => w.widget_id === widgetId);
    if (!widget) return;
    try {
      const formBody = {
        UserId: userId,
        WidgetId: widget.widget_id,
        LoggedInUserId: userId,
        token: token
      };
      await dispatch(UsersWidgetsDelete(formBody)).unwrap();
      fetchWidgets();
    } catch (err) { }
  };

  const performDrop = (info, dropzone, targetWidgetId) => {
    const { widgetId, from } = info;
    if (from === "tray" && dropzone === "dashboard") {
      addWidgetToDashboard(widgetId);
    } else if (from === "dashboard" && dropzone === "tray") {
      removeWidgetFromDashboard(widgetId);
    } else if (
      from === "dashboard" &&
      dropzone === "dashboard" &&
      targetWidgetId &&
      targetWidgetId !== widgetId
    ) {
      const list = [...dashboardWidgetsRef.current];
      const draggedIndex = list.findIndex(w => w.widget_id === widgetId);
      const targetIndex = list.findIndex(w => w.widget_id === targetWidgetId);
      if (draggedIndex !== -1 && targetIndex !== -1) {
        [list[draggedIndex], list[targetIndex]] = [list[targetIndex], list[draggedIndex]];
        setDashboardWidgets(list);
        // You might want to add an API call here to persist the new order
        // dispatch(UpdateWidgetOrder({ widgets: list, userId, token }));
      }
    }
  };

  const getTargetsFromPoint = (x, y) => {
    const el = document.elementFromPoint(x, y);
    if (!el) return { dropzone: null, targetWidgetId: null };
    const dropzoneEl = el.closest("[data-dropzone]");
    const widgetEl = el.closest("[data-widget-id]");
    return {
      dropzone: dropzoneEl ? dropzoneEl.getAttribute("data-dropzone") : null,
      targetWidgetId: widgetEl ? parseInt(widgetEl.getAttribute("data-widget-id")) : null,
    };
  };

  const isModalOpen = () => Boolean(document.querySelector(".modal.show"));

  const isTextInteractionTarget = (target) => {
    if (!(target instanceof Element)) return false;
    return Boolean(
      target.closest(
        'input, textarea, select, button, a, label, [contenteditable="true"], .form-control, .custom-textarea, .modal, .swal2-container, .dropdown-menu'
      )
    );
  };

  const handleWidgetMouseDown = (e, widgetId, from) => {
    if (e.button !== 0) return;
    if (isModalOpen()) return;
    if (isTextInteractionTarget(e.target)) return;
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    dragRef.current = {
      widgetId,
      from,
      el,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      started: false,
    };

    const applyClonePosition = () => {
      rafRef.current = null;
      const info = dragRef.current;
      const node = cloneElRef.current;
      if (!info || !node) return;
      const { x, y } = latestPosRef.current;
      node.style.transform =
        `translate3d(${x - info.offsetX}px, ${y - info.offsetY}px, 0) rotate(0deg) scale(1)`;
    };

    const handleMouseMove = (ev) => {
      const info = dragRef.current;
      if (!info) return;
      if (isModalOpen()) {
        dragRef.current = null;
        setIsDragging(false);
        setHoverTargetId(null);
        setActiveDragId(null);
        setDragClone(null);
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        return;
      }
      if (!info.started) {
        const dx = Math.abs(ev.clientX - info.startX);
        const dy = Math.abs(ev.clientY - info.startY);
        if (dx < 5 && dy < 5) return;
        info.started = true;
        latestPosRef.current = { x: ev.clientX, y: ev.clientY };
        setIsDragging(true);
        setActiveDragId(info.widgetId);
        setDragClone({
          html: info.el.innerHTML,
          width: info.width,
          height: info.height,
          offsetX: info.offsetX,
          offsetY: info.offsetY,
          className: info.el.className,
        });
      }
      latestPosRef.current = { x: ev.clientX, y: ev.clientY };
      if (rafRef.current == null) {
        rafRef.current = window.requestAnimationFrame(applyClonePosition);
      }
      const { targetWidgetId } = getTargetsFromPoint(ev.clientX, ev.clientY);
      setHoverTargetId(targetWidgetId && targetWidgetId !== info.widgetId ? targetWidgetId : null);
    };

    const handleMouseUp = (ev) => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      const info = dragRef.current;
      dragRef.current = null;
      setIsDragging(false);
      setHoverTargetId(null);
      setActiveDragId(null);
      setDragClone(null);
      if (!info || !info.started) return;
      const { dropzone, targetWidgetId } = getTargetsFromPoint(ev.clientX, ev.clientY);
      performDrop(info, dropzone, targetWidgetId);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const renderWidget = (widget, from) => {
    const WidgetComponent = props.widgetComponentMap[widget.widget_component];
    if (!WidgetComponent) return null;
    return (
      <div
        className={from === 'tray' ? 'col-md-12' : 'col-md-6 col-lg-6 col-xl-3'}
        data-widget-id={widget.widget_id}
        data-from={from}
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        onMouseDown={(e) => handleWidgetMouseDown(e, widget.widget_id, from)}
        style={{
          cursor: isDragging ? 'move' : 'grab',
          border: hoverTargetId === widget.widget_id ? '2px dashed var(--pmr-secondary)' : undefined,
          opacity: activeDragId === widget.widget_id ? 0 : 1,
          transform: hoverTargetId === widget.widget_id ? 'scale(1.02)' : undefined,
          transition: 'opacity 0.15s ease, transform 0.15s ease',
        }}
      >
        <div key={widget.widget_id}>
          <WidgetComponent
            userId={userId}
            widgetWidth='100%'
            className='col-md-12 col-lg-12'
            showAddButton={from === "tray"}
            showRemoveButton={from === "dashboard"}WidgetComponent
            onAdd={() => addWidgetToDashboard(widget.widget_id)}
            onRemove={() => removeWidgetFromDashboard(widget.widget_id)}
            widgetName={widget.table_name || widget.widget_name}
            data={widget}
            selectedFamilyMember={props.selectedFamilyMember}
            onRowClickforLabResults={props.onRowClickforLabResults}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="mt-2">
      {isDragging && dragClone && (
        <div
          ref={cloneElRef}
          className="dashboard-widget-drag-clone"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            display: "block",
            width: dragClone.width,
            height: dragClone.height,
            zIndex: 2000,
            pointerEvents: "none",
            opacity: 0.9,
            transform: `translate3d(${latestPosRef.current.x - dragClone.offsetX}px, ${latestPosRef.current.y - dragClone.offsetY}px, 0) rotate(0deg) scale(1)`,
            transition: "transform 90ms cubic-bezier(0.22, 1, 0.36, 1), opacity 150ms ease",
            willChange: "transform",
            borderRadius: "8px",
            boxSizing: "border-box",
          }}
          dangerouslySetInnerHTML={{ __html: dragClone.html }}
        />
      )}
      <div className="dashboard-layout">
        {isLoading && <div className="loading-overlay">Loading...</div>}
        {error && <div className="alert alert-danger">{error}</div>}
        {props.showTrayButton !== false && (
          <button
            onClick={() => setShowTray(prev => !prev)}
            className={`toggle-tray-button ${showTray ? "tray-open" : ""}`}
            aria-label={showTray ? "Close widget tray" : "Open widget tray"}
            title="Widget Tray"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '50px',
              minHeight: '50px',
              padding: '12px 16px',
              fontSize: '1.2rem',
              fontWeight: 'bold', 
              cursor: 'pointer'
            }}
          >
            {showTray ? "→" : "←"}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/>
              <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/>
              <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/>
              <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/>
            </svg>
          </button>
        )}
        <div className="dashboard-content col-md-12 col-lg-12">
          <div
            className="dashboard-container style"
            data-dropzone="dashboard"
            style={{
              marginLeft: '0.1rem',
              marginRight: '0.1rem',
            }}
          >
            <div className="empty-drop-zone col">
              Choose additional widgets from the <span className="text-primary fs-5" style={{ color: "var(--pmr-secondary)", fontWeight: "bold" }}>widget tray.</span>
            </div>
            <div className="dashboard-widgets-grid horizontal-layout2 mt-2">
              <div className="row">
                {dashboardWidgets.map((widget) =>
                  renderWidget(widget, "dashboard")
                )}
              </div>
            </div>
          </div>
        </div>
        <div
          className={`tray-panel ${showTray ? "open" : ""}`}
          data-dropzone="tray"
          style={{ zIndex: 1050 }}
        >
          <div className="tray-header d-flex justify-content-between align-items-center p-2 bg-light border-bottom" style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: "#f8f9fa !important"
            }}>
            <h5 className="tray-title mb-0 text-capitalize fw-bold" >📦 Widget Tray</h5>
            <button
              className="btn-close"
              onClick={() => setShowTray(false)}
              aria-label="Close widget tray"
              style={{
                background: "none",
                border: "none",
                fontSize: "1.5rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                padding: 0,
                margin: 0,
                lineHeight: 1
              }}
            >
              ×
            </button>
          </div>
          <div className="tray-widgets-container d-flex flex-wrap p-3 col-md-12 col-lg-12" >
            {trayWidgets.map((widget) =>
              renderWidget(widget, "tray")
            )}
          </div>
        </div>
      </div>
    </div >
  );
};

export default DashboardWidgetManager;
